import { useId } from "react";
import { Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useDraft } from "@/components/itsm-config/draft-context";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import { ScopeSelect } from "@/components/itsm-config/scope-select";
import {
  useFreshserviceTicketTypes,
  useSnowTicketTypes,
} from "@/hooks/use-itsm-metadata";
import {
  agentActsOnNothing,
  isInvalidAgentRule,
} from "@/lib/itsm-config/agent-rules";
import {
  AGENT_TICKET_TYPES,
  DEFAULT_AGENT_CONFIG,
  type AgentChannel,
  type AgentConfig,
  type AgentMatch,
  type AgentRule,
  type AgentTicketType,
  type ItsmProviderFamily,
  type ProviderConfig,
} from "@/lib/types/itsm-config";

interface Props {
  tenantId: string;
  credentialId: string;
  family: ItsmProviderFamily;
  providerConfig: ProviderConfig;
}

const ANY = "__any__";

const TICKET_TYPE_OPTIONS = AGENT_TICKET_TYPES.map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}));

/** Empty means unset. Not trimmed: the inputs are controlled, and trimming
 *  on every keystroke would eat the space being typed. */
function emptyToNull(value: string): string | null {
  return value === "" ? null : value;
}

const CATALOG_ITEM_HINT: Record<ItsmProviderFamily, string> = {
  servicenow: "cat_item sys_id",
  jira: "request type id",
  freshservice: "service item id",
};

/** External record types the provider can list; free text otherwise. */
function useExternalTypeOptions(props: Props) {
  const snow = useSnowTicketTypes(
    props.family === "servicenow" ? props.tenantId : undefined,
    props.credentialId,
  );
  const fs = useFreshserviceTicketTypes(
    props.family === "freshservice" ? props.tenantId : undefined,
    props.credentialId,
  );
  if (props.family === "servicenow") {
    return { options: snow.data?.options ?? [], isLoading: snow.isLoading };
  }
  if (props.family === "freshservice") {
    return { options: fs.data?.options ?? [], isLoading: fs.isLoading };
  }
  return { options: [], isLoading: false };
}

/** Any ITSM provider: the AI agent on tickets imported from this
 *  integration. Replaces the harmony:itsm:agent-bridge flag. */
export function AgentSection(props: Props) {
  const { update } = useDraft();
  const agent = props.providerConfig.agent;
  const enabled = agent?.enabled ?? false;
  const channel: AgentChannel = agent?.channel ?? "ticket";
  const rules = agent?.rules ?? [];
  const externalTypes = useExternalTypeOptions(props);
  const enabledId = useId();
  const enabledNoteId = useId();
  const channelId = useId();
  const channelNoteId = useId();
  const rulesNoteId = useId();
  // Freshservice tickets record no scope, so a scope rule would never match;
  // the column still shows when one was set, so it can be cleared.
  const showScope =
    props.family !== "freshservice" ||
    rules.some((r) => Boolean(r.match?.provider_scope));
  const itemHint = CATALOG_ITEM_HINT[props.family];
  const actsOnNothing = enabled && agentActsOnNothing(rules);

  const save = (next: AgentConfig) => {
    update({ agent: next });
  };
  const current: AgentConfig = { enabled, channel, rules };
  const setRules = (next: AgentRule[]) => {
    save({ ...current, rules: next });
  };
  const patchRule = (index: number, patch: Partial<AgentRule>) => {
    setRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };
  const patchMatch = (index: number, patch: Partial<AgentMatch>) => {
    patchRule(index, { match: { ...rules.at(index)?.match, ...patch } });
  };

  const toggleEnabled = (on: boolean) => {
    // Enabling an integration that never had the agent starts from incidents
    // only; an existing block keeps its channel and rules.
    save(on && !agent ? DEFAULT_AGENT_CONFIG : { ...current, enabled: on });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI agent</CardTitle>
        <CardDescription>
          Whether Harmony AI works tickets imported from this integration,
          which ones, and where it talks to the requester. Historic imports
          never reach it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-start gap-3 rounded-md border px-4 py-3">
          <div className="flex-1 space-y-0.5">
            <p id={enabledId} className="text-sm font-medium">
              Agent works imported tickets
            </p>
            <p id={enabledNoteId} className="text-muted-foreground text-sm">
              {enabled
                ? "On — new live-imported tickets go to the agent, and new " +
                  "comments from the provider wake it."
                : "Off — imported tickets never reach the agent."}
            </p>
          </div>
          <Switch
            checked={enabled}
            aria-labelledby={enabledId}
            aria-describedby={enabledNoteId}
            onCheckedChange={toggleEnabled}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={channelId}>Channel</Label>
          <Select
            value={channel}
            disabled={!enabled}
            onValueChange={(v) => {
              save({ ...current, channel: v as AgentChannel });
            }}
          >
            <SelectTrigger
              id={channelId}
              aria-describedby={channelNoteId}
              className="w-64"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ticket">In the ticket</SelectItem>
              <SelectItem value="slack">Slack DM</SelectItem>
            </SelectContent>
          </Select>
          <p id={channelNoteId} className="text-muted-foreground text-sm">
            {channel === "slack"
              ? "The agent DMs the requester in Slack and the conversation " +
                "continues there. Every message is mirrored to the provider " +
                "as an internal note. Requesters without a Slack user are " +
                "answered on the ticket."
              : "The agent replies on the ticket, as comments in the provider."}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Which tickets</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!enabled}
              onClick={() => {
                setRules([...rules, { match: {}, act: true }]);
              }}
            >
              <Plus className="mr-1 size-4" /> Add rule
            </Button>
          </div>
          <p id={rulesNoteId} className="text-muted-foreground text-sm">
            {rules.length === 0
              ? enabled
                ? "No rules — the agent works every imported ticket."
                : "No rules."
              : "The most specific matching rule decides; a ticket no rule " +
                "matches is left alone. Narrow a rule to carve an exception " +
                "out of a broader one."}
          </p>
          {actsOnNothing && (
            <p role="status" className="text-destructive text-sm">
              No rule has the agent work tickets, so it works none. Add a rule
              the agent works (an empty one matches everything) and keep the
              others as exceptions.
            </p>
          )}
          {rules.length > 0 && (
            <Table aria-describedby={rulesNoteId}>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket type</TableHead>
                  <TableHead>Provider record type</TableHead>
                  <TableHead>Catalog item</TableHead>
                  {showScope && <TableHead>Scope</TableHead>}
                  <TableHead>Agent works it</TableHead>
                  <TableHead className="sr-only">Remove</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule, index) => {
                  const match = rule.match ?? {};
                  const act = rule.act ?? true;
                  const invalid = isInvalidAgentRule(rule);
                  const n = index + 1;
                  const errorId = `${rulesNoteId}-rule-${n}-error`;
                  return (
                    // eslint-disable-next-line react-x/no-array-index-key -- config rules have no stable id
                    <TableRow key={index}>
                      <TableCell>
                        <Select
                          value={match.issue_type ?? ANY}
                          disabled={!enabled}
                          onValueChange={(v) => {
                            patchMatch(index, {
                              issue_type:
                                v === ANY ? null : (v as AgentTicketType),
                            });
                          }}
                        >
                          <SelectTrigger
                            className="w-32"
                            aria-label={`Rule ${n} ticket type`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={ANY}>Any</SelectItem>
                            {TICKET_TYPE_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <MetadataSelect
                          options={externalTypes.options}
                          isLoading={externalTypes.isLoading}
                          value={match.external_issue_type ?? ""}
                          disabled={!enabled}
                          placeholder="Any"
                          aria-label={`Rule ${n} provider record type`}
                          onChange={(v) => {
                            patchMatch(index, {
                              external_issue_type: emptyToNull(v),
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell className="space-y-1">
                        <Input
                          value={match.catalog_item_id ?? ""}
                          disabled={!enabled}
                          placeholder={`${itemHint} (any)`}
                          aria-label={`Rule ${n} catalog item id`}
                          onChange={(e) => {
                            const id = emptyToNull(e.target.value);
                            patchMatch(index, {
                              catalog_item_id: id,
                              catalog_item_name: id
                                ? (match.catalog_item_name ?? null)
                                : null,
                            });
                          }}
                        />
                        <Input
                          value={match.catalog_item_name ?? ""}
                          disabled={!enabled || !match.catalog_item_id}
                          placeholder="Name (display only)"
                          aria-label={`Rule ${n} catalog item name`}
                          onChange={(e) => {
                            patchMatch(index, {
                              catalog_item_name: emptyToNull(e.target.value),
                            });
                          }}
                        />
                      </TableCell>
                      {showScope && (
                        <TableCell>
                          <ScopeSelect
                            family={props.family}
                            tenantId={props.tenantId}
                            integrationId={props.credentialId}
                            value={match.provider_scope ?? ""}
                            placeholder="Any"
                            aria-label={`Rule ${n} scope`}
                            disabled={!enabled}
                            onChange={(v) => {
                              patchMatch(index, {
                                provider_scope: emptyToNull(v),
                              });
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Switch
                          checked={act}
                          disabled={!enabled}
                          aria-label={`Rule ${n}: agent works matching tickets`}
                          aria-invalid={invalid}
                          aria-describedby={invalid ? errorId : undefined}
                          onCheckedChange={(v) => {
                            patchRule(index, { act: v });
                          }}
                        />
                        {invalid && (
                          <p
                            id={errorId}
                            className="text-destructive text-xs"
                          >
                            A rule the agent skips must name a type, item or
                            scope.
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={!enabled}
                          aria-label={`Remove rule ${n}`}
                          onClick={() => {
                            setRules(rules.filter((_, i) => i !== index));
                          }}
                        >
                          <X className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <p className="text-muted-foreground text-xs">
            Catalog items are matched by the provider&apos;s id (the{" "}
            {itemHint}); the name is only shown here. Rules on record type,
            catalog item or scope match tickets imported from now on.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
